/**
 * Glyph icons for local macOS apps (white on tinted tile).
 * Apple publishes no brand SVGs, so these are simple monochrome glyphs.
 * No external dependencies.
 */

interface IconProps {
  size?: number;
}

function glyphProps(size: number) {
  return {
    viewBox: "0 0 24 24",
    width: size,
    height: size,
    fill: "none",
    stroke: "#fff",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  } as const;
}

export function FinderIcon({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="#fff">
      <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5h3.6l2 2.4h7.4a2.5 2.5 0 0 1 2.5 2.5v7.1a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17z" />
    </svg>
  );
}

export function TextEditIcon({ size = 22 }: IconProps) {
  return (
    <svg {...glyphProps(size)}>
      <path d="M6 3h7l4 4v14H6z" />
      <path d="M13 3v4h4" />
      <path d="M9 13.5h6M9 17h6" />
    </svg>
  );
}

export function PreviewIcon({ size = 22 }: IconProps) {
  return (
    <svg {...glyphProps(size)}>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M15 15l5.5 5.5" />
    </svg>
  );
}

export function TerminalIcon({ size = 22 }: IconProps) {
  return (
    <svg {...glyphProps(size)}>
      <rect x="3" y="4.5" width="18" height="15" rx="3" />
      <path d="M7 9.5l3 3-3 3M12.5 15.5H17" />
    </svg>
  );
}

export function SafariIcon({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <circle cx="12" cy="12" r="8.5" fill="none" stroke="#fff" strokeWidth={1.8} />
      <path d="M15.5 8.5l-2.3 5.2-5.7 1.8 2.3-5.2z" fill="#fff" />
    </svg>
  );
}

export function ChromeIcon({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <circle cx="12" cy="12" r="8.5" fill="none" stroke="#fff" strokeWidth={1.8} />
      <circle cx="12" cy="12" r="3" fill="#fff" />
      <path d="M12 3.5V9M4.6 16.2l5-2.6M19.4 16.2l-5-2.6" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
    </svg>
  );
}

export function MailIcon({ size = 22 }: IconProps) {
  return (
    <svg {...glyphProps(size)}>
      <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
      <path d="M4 7.5l8 6 8-6" />
    </svg>
  );
}

export function CalendarIcon({ size = 22 }: IconProps) {
  return (
    <svg {...glyphProps(size)}>
      <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    </svg>
  );
}

export function MessagesIcon({ size = 22 }: IconProps) {
  return (
    <svg {...glyphProps(size)}>
      <path d="M4 6.5A3.5 3.5 0 0 1 7.5 3h9A3.5 3.5 0 0 1 20 6.5v6a3.5 3.5 0 0 1-3.5 3.5H12l-4.5 3.5v-3.5H7.5A3.5 3.5 0 0 1 4 12.5z" />
    </svg>
  );
}

export function MusicIcon({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="#fff">
      <path d="M9 18.5a3 3 0 1 1-2-2.83V6.2c0-.44.28-.83.7-.97l9-3A1 1 0 0 1 18 3.2v11.3a3 3 0 1 1-2-2.83V7.1l-7 2.33z" />
    </svg>
  );
}

export function NotesIcon({ size = 22 }: IconProps) {
  return (
    <svg {...glyphProps(size)}>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 8.5h6M9 12h6M9 15.5h4" />
    </svg>
  );
}

export function RemindersIcon({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <circle cx="6.5" cy="7" r="1.6" fill="#fff" />
      <circle cx="6.5" cy="12" r="1.6" fill="#fff" />
      <circle cx="6.5" cy="17" r="1.6" fill="#fff" />
      <path d="M11 7h9M11 12h9M11 17h9" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
    </svg>
  );
}

export function PhotosIcon({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" stroke="#fff" strokeWidth={1.8} />
      <circle cx="9" cy="10" r="1.6" fill="#fff" />
      <path d="M4 17l5-5 3.5 3.5L16 12l4 5z" fill="#fff" />
    </svg>
  );
}

export function ContactsIcon({ size = 22 }: IconProps) {
  return (
    <svg {...glyphProps(size)}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c1.2-3.6 3.8-5.5 7-5.5s5.8 1.9 7 5.5" />
    </svg>
  );
}

export function MapsIcon({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <path
        d="M12 21s-6.5-5.4-6.5-10.5A6.5 6.5 0 0 1 12 4a6.5 6.5 0 0 1 6.5 6.5C18.5 15.6 12 21 12 21z"
        fill="none"
        stroke="#fff"
        strokeWidth={1.8}
      />
      <circle cx="12" cy="10.5" r="2.2" fill="#fff" />
    </svg>
  );
}

export function SystemSettingsIcon({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <path d="M4 6.5h16M4 12h16M4 17.5h16" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
      <circle cx="9" cy="6.5" r="2.4" fill="#fff" />
      <circle cx="15" cy="12" r="2.4" fill="#fff" />
      <circle cx="8" cy="17.5" r="2.4" fill="#fff" />
    </svg>
  );
}

export function VSCodeIcon({ size = 22 }: IconProps) {
  return (
    <svg {...glyphProps(size)}>
      <path d="M8 7l-5 5 5 5M16 7l5 5-5 5M13.5 5l-3 14" />
    </svg>
  );
}

export type { IconProps };
